/**
 * Ce qui varie d'une collection à l'autre dans l'historique — et rien d'autre.
 *
 * Le composant `AuditLogs` est unique et ne connaît aucune collection : il lit ce fichier. Auditer une
 * collection de plus, c'est ajouter une entrée ici ; une collection absente s'affiche avec son nom
 * physique et ses chemins bruts, ce qui reste lisible et ne casse jamais.
 */
export type AuditDisplayConfig = {
    /**
     * Libellé d'un champ, par chemin de patch. Un chemin absent s'affiche tel quel.
     *
     * Le NOM DE LA COLLECTION, lui, n'est jamais traduit : seules les collections listées ici auraient
     * eu un nom français, les autres restaient en `users` ou `deliveryPlanning` — une moitié traduite
     * est moins lisible qu'aucune. Le nom physique est de toute façon celui qu'on cherche quand on
     * remonte une trace jusqu'à la base.
     */
    fieldLabels?: Record<string, string>;
    /**
     * Préfixe de la route de détail, pour rendre cliquable le document depuis la page Audit.
     * Absent quand la collection n'a pas de page à elle (les gabarits d'étagère et d'emplacement
     * s'éditent depuis le gabarit de colonne).
     */
    detailPath?: string;
};

export const AUDIT_DISPLAY: Record<string, AuditDisplayConfig> = {
    categories: {
        detailPath: '/category/',
        fieldLabels: {
            '/title': 'Titre',
            '/indexDisplay': 'Ordre d’affichage',
            '/state': 'Statut',
            '/imageUrl': 'Image',
        },
    },
    equipmentCategories: {
        fieldLabels: { '/title': 'Titre', '/displayIndex': 'Ordre d’affichage', '/state': 'Statut' },
    },
    mealPlanDefinitions: {
        detailPath: '/meal-plan-definition/',
        fieldLabels: {
            '/name': 'Nom',
            '/discountRatios': 'Remises',
            '/eligibleCategories': 'Emplacements',
            '/hoursIntervalAvailable': 'Horaire de validité',
            '/images': 'Visuels',
            '/state': 'Statut',
        },
    },
    organizations: {
        detailPath: '/organization/',
        fieldLabels: {
            '/name': 'Nom',
            '/siret': 'SIRET',
            '/hasSubscription': 'Abonnement livraison',
            '/B2BPriceRange': 'Gamme de prix B2B',
            '/odooOrganizationId': 'Société Odoo',
        },
    },
    sites: {
        detailPath: '/site/',
        fieldLabels: {
            '/name': 'Nom',
            '/organizationId': 'Organisation',
            '/state': 'Statut',
            '/deliveryZoneId': 'Zone de livraison',
            '/B2CPriceRange': 'Gamme de prix B2C',
            '/launchDate': 'Date de lancement',
            '/removalDate': 'Date de retrait',
            '/accessTime': 'Temps d’accès',
            '/deliveryDays': 'Jours de livraison',
        },
    },
    totems: {
        detailPath: '/totem/',
        fieldLabels: {
            '/name': 'Nom',
            '/organizationId': 'Organisation',
            '/address': 'Adresse',
            '/city': 'Ville',
            '/consigneDelivery': 'Consigne de livraison',
        },
    },
    products: {
        detailPath: '/product/',
        fieldLabels: {
            '/fullname': 'Nom complet',
            '/name': 'Nom',
            '/allergen': 'Allergènes',
            '/categoryId': 'Catégorie',
            '/subcategoryId': 'Sous-catégorie',
            '/state': 'Statut',
            '/imageUrl': 'Image',
            '/rating': 'Note moyenne',
            '/nbRatings': 'Nombre de notes',
        },
    },
    priceRangeProducts: {
        fieldLabels: {
            '/price': 'Prix',
            '/crossedPrice': 'Prix barré',
            '/rangeId': 'Gamme de prix',
            '/state': 'Statut',
        },
    },
    productPriceRanges: {
        fieldLabels: { '/name': 'Nom', '/productArrangements': 'Gammes de produits' },
    },
    productArrangements: {
        fieldLabels: {
            '/name': 'Nom',
            '/reassortmentType': 'Type de réassort',
            '/productIdsWithStockMax': 'Produits',
            '/state': 'Statut',
        },
    },
    productGroups: {
        fieldLabels: { '/name': 'Nom', '/productIds': 'Produits', '/state': 'Statut' },
    },
    ordersSupply: {
        detailPath: '/supply-order/',
        fieldLabels: { '/state': 'Statut', '/supplierId': 'Fournisseur', '/invoiceNumber': 'Numéro de facture' },
    },
    microstoreColumns: {
        detailPath: '/microstore-column/',
        fieldLabels: {
            '/name': 'Nom',
            '/state': 'Statut',
            '/isPaused': 'En pause',
            '/positionInColumnGroup': 'Position dans le groupement',
            '/microstoreColumnTemplateId': 'Gabarit',
        },
    },
    microstoreColumnGroups: {
        detailPath: '/microstore-column-group/',
        fieldLabels: {
            '/name': 'Nom',
            '/positionInSite': 'Position sur le site',
            '/avaSalePlaceLocationId': 'Point de vente AVA',
        },
    },
    microstoreColumnTemplates: {
        detailPath: '/microstore-column-template/',
        fieldLabels: { '/name': 'Nom', '/type': 'Type', '/state': 'Statut' },
    },
    deliveries: {
        detailPath: '/delivery/',
        fieldLabels: {
            '/state': 'Statut',
            '/type': 'Type',
            '/deliveryDate': 'Date de livraison',
            '/siteId': 'Site',
            '/deliveryPersonUserId': 'Livreur',
            '/roundName': 'Tournée',
            '/sequence': 'Ordre dans la tournée',
            '/driversComment': 'Commentaire du livreur',
            '/orderIds': 'Commandes',
        },
    },
    deliveryPlanning: {
        // Ni `/siteId` ni `/date` : ils forment la clé naturelle du document, donc ils ne changent
        // jamais et n'apparaissent dans aucun patch. Ils arrivent par le LIBELLÉ DU SUJET, que la page
        // d'historique résout (cf. `DeliveryPlanningAudit`).
        fieldLabels: { '/isActive': 'Livraison prévue', '/isManual': 'Saisie manuelle' },
    },
    boxes: {
        fieldLabels: {
            '/state': 'Statut',
            '/position': 'Position dans le bundle',
            '/number': 'Numéro',
            '/type': 'Type',
            '/containerType': 'Type de contenant',
            '/deliveryId': 'Livraison',
            '/bundleId': 'Bundle',
            '/orderId': 'Commande',
            '/columnId': 'Colonne',
            '/warehouseColumnId': 'Colonne entrepôt',
            '/isFull': 'Pleine',
            '/products': 'Produits',
        },
    },
    bundles: {
        detailPath: '/bundle/',
        fieldLabels: {
            '/state': 'Statut',
            '/type': 'Flux',
            '/number': 'Numéro',
            '/containerType': 'Type de contenant',
            '/closingDate': 'Date de fermeture',
            '/scheduledPreparationDate': 'Date de préparation prévue',
            '/operatorId': 'Opérateur',
            '/warehouseColumnId': 'Colonne entrepôt',
        },
    },
    orders: {
        detailPath: '/order/',
        fieldLabels: {
            '/state': 'Statut',
            // Le champ porte son nom historique en snake_case dans mongo.
            '/date_delivery': 'Date de livraison',
            '/number': 'Numéro',
            '/totemId': 'TOTEM',
            '/userId': 'Client',
            '/organizationId': 'Organisation',
            '/discount': 'Réduction',
            '/isPunctual': 'Commande ponctuelle',
            '/prices': 'Prix',
            '/products': 'Produits',
        },
    },
    opsParameters: {
        // Un seul document, et TOUT y vit sous `parameters` : les chemins arrivent donc imbriqués
        // (`/parameters/serviceTimePerBox`) et s'affichent bruts — lisibles, mais pas traduits. Les
        // libeller demanderait une entrée par paramètre, ce qui ferait une seconde liste à tenir à
        // jour en parallèle du formulaire.
        fieldLabels: { '/parameters': 'Paramètres' },
    },
    subventions: {
        detailPath: '/subvention/',
        fieldLabels: {
            '/name': 'Nom',
            '/currentAmount': 'Montant',
            '/reload': 'Rechargement',
            '/organizationId': 'Organisation',
            '/billToOrganizationId': 'Organisation refacturée',
            '/priority': 'Priorité',
            '/cumulative': 'Cumulable',
            '/categories': 'Catégories',
            '/validityPeriod': 'Période de validité',
            '/archivedAt': 'Archivage',
        },
    },
    tags: {
        detailPath: '/tag/',
        fieldLabels: { '/name': 'Nom', '/type': 'Type', '/state': 'Statut', '/displayIndex': 'Ordre d’affichage' },
    },
    badges: {
        detailPath: '/badge/',
        fieldLabels: { '/name': 'Nom', '/type': 'Type', '/imageUrl': 'Image' },
    },
    equipments: {
        detailPath: '/equipment/',
        fieldLabels: {
            '/name': 'Nom',
            '/brand': 'Marque',
            '/state': 'Statut',
            '/equipmentCategoryId': 'Catégorie',
            '/equipmentSubcategoryId': 'Sous-catégorie',
            '/suppliers': 'Fournisseurs',
            '/price': 'Prix',
            '/hidden': 'Masqué',
            '/imageUrl': 'Image',
        },
    },
    rituals: {
        detailPath: '/ritual/',
        fieldLabels: {
            '/name': 'Nom',
            '/state': 'Statut',
            '/type': 'Type',
            '/activeDate': 'Date',
            '/activeWeekdays': 'Jours actifs',
            '/repetition': 'Répétition',
            '/timeWindow': 'Horaire',
            '/associatedDiscount': 'Réduction associée',
            '/restrictedSiteIds': 'Sites concernés',
            '/restrictedOrganizationIds': 'Organisations concernées',
            '/billToOrganizationId': 'Organisation refacturée',
        },
    },
    polls: {
        detailPath: '/poll/',
        fieldLabels: {
            '/title': 'Titre',
            '/question': 'Question',
            '/state': 'Statut',
            '/answerOptions': 'Réponses possibles',
            '/dateWindow': 'Période',
            '/audience': 'Audience',
        },
    },
    microstoreShelves: {
        fieldLabels: { '/positionInColumn': 'Position dans la colonne', '/state': 'Statut' },
    },
    microstoreLocations: {
        fieldLabels: {
            '/productId': 'Produit',
            '/replacementProductId': 'Produit de remplacement',
            '/stockToAdd': 'Stock à ajouter',
            '/targetStock': 'Stock cible',
            '/nayaxMDBCode': 'Code MDB Nayax',
            '/state': 'Statut',
        },
    },
    microstoreShelfTemplates: {
        fieldLabels: { '/positionInColumn': 'Position dans la colonne', '/state': 'Statut' },
    },
    microstoreLocationTemplates: {
        fieldLabels: {
            '/productId': 'Produit',
            '/productArrangementId': 'Gamme de produits',
            '/stockMax': 'Stock maximum',
            '/trayMaxCapacity': 'Capacité du plateau',
            '/state': 'Statut',
        },
    },
};

export const getAuditDisplay = (collectionName: string): AuditDisplayConfig | undefined =>
    AUDIT_DISPLAY[collectionName];
